/**
 * Phase 8a — Pre-Mortem Engine
 *
 * Composite risk scorer that predicts which jobs will fail in the next 7 days
 * by reading 10 weighted signals already present in the system. Pure function:
 * no side effects, no UI, no infrastructure.
 *
 * Output: ranked PredictedFailure[] above a confidence threshold. On calm
 * days, the engine returns an empty list — silence is a valid answer.
 *
 * The engine does NOT just say "this job is at risk." It names a specific
 * failure mode and offers a pre-drafted intervention. That's the moat.
 */

import type { Job, Issue } from "@/data/types";
import type { FollowUpTask, Opportunity } from "@/data/opportunityTypes";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import { computeServiceCallRisk } from "@/data/serviceCallPrevention";
import { getJobHealth } from "@/data/jobHealth";
import {
  checkFinancialIntegrity,
  detectPostCompletionFinancialException,
  deriveJobState,
  type PostCompletionFinancialException,
} from "@/data/jobStateDerived";
import { deriveReadinessFromJob } from "@/data/readiness";

// ── Public Types ────────────────────────────────────────────────────────

export type PredictedFailureMode =
  | "material_will_miss_install"
  | "permit_will_block_schedule"
  | "customer_about_to_escalate"
  | "install_will_callback"
  | "financial_will_block_closeout"
  | "followup_dropping_ball"
  | "readiness_degrading"
  | "duplicate_attention_chaos"
  | "crew_unreliable_for_install"
  | "unresolved_critical_issue"
  | "build_review_stalled"
  // CCC: post-completion next-morning financial exposure
  | "payment_not_collected_post_completion"
  | "funding_not_confirmed_post_completion";

/** Threshold (days) before a job sitting in Build Review is treated as stalled. */
export const BUILD_REVIEW_STALL_DAYS = 2;

/** Right of Rescission window after sale signing (CCC: 3 calendar days). */
export const RESCISSION_DAYS = 3;

export interface RiskSignal {
  /** Stable id of the signal — useful for tests & analytics. */
  key: string;
  /** Human label shown in "Why I'm flagging this" list. */
  label: string;
  /** Weight contributed to composite score (0-100 scale). */
  weight: number;
  /** Whether this signal fired for the job. */
  present: boolean;
}

export interface PredictedFailure {
  jobId: string;
  customerName: string;
  /** Composite 0-100 risk score. */
  confidence: number;
  /** The single most likely failure mode driving this prediction. */
  failureMode: PredictedFailureMode;
  /** Plain-English headline shown front and center. */
  headline: string;
  /** When the predicted failure would manifest, if known. */
  predictedBy?: string;
  /** Ordered evidence list — only signals that fired. */
  evidence: RiskSignal[];
  /** Pre-drafted intervention. */
  intervention: {
    /** Short verb-led label for the primary CTA. */
    label: string;
    /** Drafted message body, when the action is a comm. */
    draft?: string;
    /** Route to deep-link the operator into the relevant module. */
    route?: string;
    /** Free-text instruction for non-comm interventions. */
    instruction?: string;
  };
  /**
   * When true, this prediction is a trend/meta-signal that should NOT occupy
   * the primary "One Thing" slot — but remains visible in "Show all" so the
   * operator keeps awareness. Set for failure modes whose interventions are
   * not single-click closures (e.g. readiness_degrading, duplicate_attention_chaos).
   */
  suppressedFromPrimary?: boolean;
}

/** Failure modes demoted out of the primary One Thing slot — they remain
 *  visible in "Show all" but never become the surfaced prediction. */
const SUPPRESSED_PRIMARY_MODES: ReadonlySet<PredictedFailureMode> = new Set([
  "readiness_degrading",
  "duplicate_attention_chaos",
]);

export interface PreMortemEngineInputs {
  jobs: Job[];
  issues: Issue[];
  followUpTasks: FollowUpTask[];
  /** Optional for backward-compat; pass real events for accurate readiness. */
  buildReviewEvents?: BuildReviewEvent[];
  /** Optional: opportunities used to anchor the rescission clock via wonAt. */
  opportunities?: Opportunity[];
  wrapRecords?: InstallerWrapRecord[];
  /** Optional: messages with last activity per job for comm-gap detection. */
  messageActivity?: Record<string, { lastInboundAt?: string; lastOutboundAt?: string }>;
}

export interface PreMortemResult {
  /** Jobs predicted to fail, ranked by confidence DESC. */
  predictions: PredictedFailure[];
  /** True when no job crossed the confidence floor. */
  allClear: boolean;
  /** Number of jobs evaluated. */
  evaluatedCount: number;
}

// ── Configuration ───────────────────────────────────────────────────────

/**
 * Confidence floor. Below this value the engine stays quiet — silence is
 * a more honest product than low-confidence noise.
 */
export const CONFIDENCE_FLOOR = 60;

const HORIZON_DAYS = 7;
const HORIZON_MS = HORIZON_DAYS * 24 * 60 * 60 * 1000;

// ── Helpers ─────────────────────────────────────────────────────────────

function hoursSince(date: string | undefined): number {
  if (!date) return Infinity;
  return (Date.now() - new Date(date).getTime()) / 36e5;
}

function daysUntil(date: string | undefined): number {
  if (!date) return Infinity;
  return (new Date(date).getTime() - Date.now()) / 864e5;
}

function withinHorizon(date: string | undefined): boolean {
  if (!date) return false;
  const dt = new Date(date).getTime() - Date.now();
  return dt > -864e5 && dt < HORIZON_MS;
}

// ── Per-job evaluation ──────────────────────────────────────────────────

function evaluateJob(
  job: Job,
  ctx: PreMortemEngineInputs,
): PredictedFailure | null {
  // ── CCC post-completion exception ──
  // Completed jobs are normally silent. The one exception: real cash or
  // funding exposure detected the morning after completion. This runs
  // BEFORE the early-return so completed-but-unpaid jobs still surface.
  if (job.currentStage === "Completed") {
    const derived = deriveJobState(job, ctx.issues, ctx.wrapRecords ?? []);
    const exception = detectPostCompletionFinancialException(job, derived.payment);
    if (!exception) return null;
    return buildPostCompletionPrediction(job, exception);
  }

  const openIssues = ctx.issues.filter(
    (i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"),
  );
  const criticalIssues = openIssues.filter((i) => i.severity === "Urgent");
  const installSoon = withinHorizon(job.scheduledInstallDate);
  const daysToInstall = daysUntil(job.scheduledInstallDate);

  const scpRisk = computeServiceCallRisk(job, ctx.issues);
  const health = getJobHealth(job, openIssues);
  const fin = checkFinancialIntegrity(job);
  const readiness = deriveReadinessFromJob(job, ctx.buildReviewEvents ?? []);
  const jobFollowUps = ctx.followUpTasks.filter(
    (t) => t.jobId === job.id && t.status !== "completed",
  );
  const overdueFollowUps = jobFollowUps.filter(
    (t) => t.dueDate && new Date(t.dueDate).getTime() < Date.now(),
  );
  const commActivity = ctx.messageActivity?.[job.id];
  const inboundUnreplied =
    commActivity?.lastInboundAt &&
    (!commActivity.lastOutboundAt ||
      new Date(commActivity.lastInboundAt) > new Date(commActivity.lastOutboundAt));
  const inboundAgeHours = inboundUnreplied ? hoursSince(commActivity!.lastInboundAt) : 0;

  // ── Build Review stall (CCC: Sale → Build Review → Rescission → Ordering) ──
  // A job parked in "Review and Validation" without an "approved"/"final_approved"
  // event blocks readiness for ordering once rescission clears. Use latest review
  // event timestamp as activity proxy; fall back to job.createdAt when absent.
  const inReviewStage = job.currentStage === "Review and Validation";
  const reviewEventsForJob = (ctx.buildReviewEvents ?? []).filter((e) => e.jobId === job.id);
  const reviewApproved = reviewEventsForJob.some(
    (e) => e.action === "approved" || e.action === "final_approved",
  );
  const lastReviewEventAt = reviewEventsForJob
    .map((e) => new Date(e.timestamp).getTime())
    .sort((a, b) => b - a)[0];
  // Anchor preference: latest BuildReviewEvent timestamp → job.dateCreated → silent.
  const jobCreatedAt = (job as unknown as { dateCreated?: string; createdAt?: string }).dateCreated
    ?? (job as unknown as { createdAt?: string }).createdAt;
  const reviewAnchorMs =
    lastReviewEventAt ??
    (jobCreatedAt ? new Date(jobCreatedAt).getTime() : undefined);
  const reviewStageAgeDays =
    reviewAnchorMs !== undefined ? (Date.now() - reviewAnchorMs) / 864e5 : 0;
  const buildReviewStalled =
    inReviewStage && !reviewApproved && reviewStageAgeDays >= BUILD_REVIEW_STALL_DAYS;

  // Rescission anchor: opportunity.wonAt (preferred) → fallback to earliest review event → job.dateCreated.
  const opportunity = (ctx.opportunities ?? []).find((o) => o.linkedJobId === job.id);
  const earliestReviewEventMs = reviewEventsForJob.length
    ? Math.min(...reviewEventsForJob.map((e) => new Date(e.timestamp).getTime()))
    : undefined;
  const saleSignedMs =
    (opportunity?.wonAt ? new Date(opportunity.wonAt).getTime() : undefined) ??
    earliestReviewEventMs ??
    (jobCreatedAt ? new Date(jobCreatedAt).getTime() : undefined);
  const rescissionEndMs =
    saleSignedMs !== undefined ? saleSignedMs + RESCISSION_DAYS * 864e5 : undefined;
  const rescissionExpired =
    rescissionEndMs !== undefined && Date.now() > rescissionEndMs;
  const daysWaiting = Math.max(0, Math.floor(reviewStageAgeDays));
  const daysBehind =
    rescissionExpired && rescissionEndMs !== undefined
      ? Math.max(0, Math.floor((Date.now() - rescissionEndMs) / 864e5))
      : 0;
  const daysUntilRescission =
    !rescissionExpired && rescissionEndMs !== undefined
      ? Math.max(0, Math.ceil((rescissionEndMs - Date.now()) / 864e5))
      : 0;
  const buildReviewContext = {
    daysWaiting,
    daysBehind,
    daysUntilRescission,
    rescissionExpired,
    hasRescissionAnchor: rescissionEndMs !== undefined,
  };

  // ── Ten weighted signals ──
  const signals: RiskSignal[] = [
    {
      key: "material_slip",
      label: "Materials not staged with install in horizon",
      weight: 20,
      present:
        installSoon &&
        daysToInstall <= 5 &&
        !["Loaded for Crew", "Staged for Job"].includes(job.materialReadiness ?? ""),
    },
    {
      key: "permit_stalled",
      label: "Permit stalled — revision needed or under review > 5d",
      weight: 18,
      present:
        !!job.permitRequired &&
        (job.permitStatus === "Revision Needed" ||
          (job.permitStatus === "Submitted" &&
            hoursSince(job.permitDetail?.submittedDate) > 5 * 24)),
    },
    {
      key: "critical_issue",
      label: `${criticalIssues.length} urgent issue(s) open`,
      weight: 16,
      present: criticalIssues.length > 0,
    },
    {
      key: "scp_high",
      label: `Service-call risk score ${scpRisk.score}`,
      weight: 14,
      present: scpRisk.score >= 40,
    },
    {
      key: "comm_gap",
      label: "Customer message unreplied > 36h",
      weight: 12,
      present: !!inboundUnreplied && inboundAgeHours > 36,
    },
    {
      key: "financial_block",
      label: "Financial integrity will block closeout",
      weight: 10,
      // CCC: only HIGH-severity discrepancies are real exposure.
      // LOW (rounding/financing artifacts) no longer fires Maverick.
      present: fin.hasHigh,
    },
    {
      key: "followup_overdue",
      label: `${overdueFollowUps.length} follow-up(s) overdue`,
      weight: 10,
      present: overdueFollowUps.length > 0,
    },
    {
      key: "readiness_blocked",
      label: `Readiness ${readiness.overall}`,
      weight: 10,
      present: readiness.overall === "blocked" || readiness.overall === "at_risk",
    },
    {
      key: "build_review_stalled",
      label: rescissionExpired
        ? `Build Review ${daysBehind}d behind rescission — ordering blocked`
        : `Build Review pending ${daysWaiting}d — ${daysUntilRescission}d until rescission clears`,
      weight: 60, // single-signal trigger: meets CONFIDENCE_FLOOR on its own
      present: buildReviewStalled,
    },
    {
      key: "crew_unreliable",
      label: "Crew rejected or unassigned with install in horizon",
      weight: 8,
      present:
        installSoon &&
        (job.dispatchStatus === "Rejected" || job.assignmentType === "Unassigned"),
    },
    {
      key: "health_problem",
      label: `Job health: ${health.status}`,
      weight: 6,
      present: health.status === "Problem",
    },
  ];

  const confidence = Math.min(
    100,
    signals.filter((s) => s.present).reduce((sum, s) => sum + s.weight, 0),
  );

  if (confidence < CONFIDENCE_FLOOR) return null;

  const evidence = signals.filter((s) => s.present);
  const failureMode = pickFailureMode(evidence, job, scpRisk.score);
  const intervention = draftIntervention(failureMode, job, evidence);
  const headline = composeHeadline(failureMode, job, buildReviewContext);

  return {
    jobId: job.id,
    customerName: job.customerName,
    confidence,
    failureMode,
    headline,
    predictedBy: job.scheduledInstallDate,
    evidence,
    intervention,
    suppressedFromPrimary: SUPPRESSED_PRIMARY_MODES.has(failureMode),
  };
}

// ── Failure mode selection ──────────────────────────────────────────────

function pickFailureMode(
  evidence: RiskSignal[],
  job: Job,
  scpScore: number,
): PredictedFailureMode {
  const has = (k: string) => evidence.some((e) => e.key === k);
  if (has("material_slip")) return "material_will_miss_install";
  // Build review stall is an upstream blocker — surface before generic readiness signals
  // but after time-critical install/permit failures.
  if (has("build_review_stalled")) return "build_review_stalled";
  if (has("permit_stalled")) return "permit_will_block_schedule";
  if (has("comm_gap")) return "customer_about_to_escalate";
  if (has("crew_unreliable")) return "crew_unreliable_for_install";
  if (has("critical_issue")) return "unresolved_critical_issue";
  if (has("financial_block")) return "financial_will_block_closeout";
  if (has("followup_overdue")) return "followup_dropping_ball";
  if (scpScore >= 40) return "install_will_callback";
  if (has("readiness_blocked")) return "readiness_degrading";
  return "duplicate_attention_chaos";
}

// ── Headline composition ────────────────────────────────────────────────

interface BuildReviewHeadlineContext {
  daysWaiting: number;
  daysBehind: number;
  daysUntilRescission: number;
  rescissionExpired: boolean;
  hasRescissionAnchor: boolean;
}

function composeHeadline(
  mode: PredictedFailureMode,
  job: Job,
  brCtx?: BuildReviewHeadlineContext,
): string {
  const dateStr = job.scheduledInstallDate
    ? new Date(job.scheduledInstallDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : null;
  switch (mode) {
    case "material_will_miss_install":
      return dateStr
        ? `${job.id} will miss install on ${dateStr} — materials not staged`
        : `${job.id} install at risk — materials not staged`;
    case "permit_will_block_schedule":
      return `${job.id} can't schedule — permit stalled`;
    case "customer_about_to_escalate":
      return `${job.id} customer escalation imminent — message unreplied`;
    case "install_will_callback":
      return `${job.id} predicted to callback after install`;
    case "financial_will_block_closeout":
      return `${job.id} closeout will block — financial gap`;
    case "followup_dropping_ball":
      return `${job.id} follow-ups slipping — handoff at risk`;
    case "readiness_degrading":
      return `${job.id} readiness degrading — install date in jeopardy`;
    case "duplicate_attention_chaos":
      return `${job.id} — multiple teams touching, no single owner`;
    case "crew_unreliable_for_install":
      return dateStr
        ? `${job.id} install on ${dateStr} has no confirmed crew`
        : `${job.id} install has no confirmed crew`;
    case "unresolved_critical_issue":
      return `${job.id} — urgent issue blocking forward motion`;
    case "build_review_stalled": {
      // Pre-deadline (rescission window still active or no anchor): waiting language.
      // Post-deadline (rescission expired): behind language with stronger urgency.
      if (!brCtx || !brCtx.hasRescissionAnchor) {
        return `${job.id} waiting on Build Review for ${BUILD_REVIEW_STALL_DAYS}+ days — not ready for ordering`;
      }
      if (brCtx.rescissionExpired) {
        return `${job.id} Build Review is ${brCtx.daysBehind}d behind — ordering blocked`;
      }
      return `${job.id} waiting on Build Review for ${brCtx.daysWaiting}d — ${brCtx.daysUntilRescission}d until rescission clears`;
    }
    case "payment_not_collected_post_completion":
      return `${job.id} — payment not received from ${job.customerName} (completed yesterday)`;
    case "funding_not_confirmed_post_completion":
      return `${job.id} — funding not confirmed for ${job.customerName} (completed yesterday)`;
  }
}

// ── Pre-drafted interventions ───────────────────────────────────────────

function draftIntervention(
  mode: PredictedFailureMode,
  job: Job,
  _evidence: RiskSignal[],
): PredictedFailure["intervention"] {
  const route = `/jobs/${job.id}`;
  switch (mode) {
    case "material_will_miss_install":
      return {
        label: "Pull materials forward",
        instruction:
          "Open warehouse view and confirm pull list, or escalate ordering for missing items.",
        route: "/warehouse-readiness",
      };
    case "permit_will_block_schedule":
      return {
        label: "Re-engage permit coordinator",
        draft: `Hey — checking on the ${job.address?.city ?? ""} permit for ${job.customerName}. Any update from the county? Need this to confirm install.`,
        route: "/permitting",
      };
    case "customer_about_to_escalate":
      return {
        label: "Reply to customer now",
        draft: `Hi ${job.customerName.split(" ")[0]} — sorry for the delayed reply. Wanted to give you a quick update on your project. Can I call you in the next hour?`,
        route,
      };
    case "install_will_callback":
      return {
        label: "Run pre-install QA review",
        instruction: "Walk the Service Call Prevention checklist before crew dispatches.",
        route,
      };
    case "financial_will_block_closeout":
      return {
        label: "Resolve financial gap",
        instruction: "Reconcile balance — closeout cannot proceed until matched.",
        route: "/accounting",
      };
    case "followup_dropping_ball":
      return {
        label: "Clear overdue follow-ups",
        route: "/follow-ups",
      };
    case "readiness_degrading":
      return {
        label: "Open readiness panel",
        route: "/install-readiness",
      };
    case "duplicate_attention_chaos":
      return {
        label: "Assign single owner",
        instruction: "Multiple teams are acting on this job. Designate one owner.",
        route,
      };
    case "crew_unreliable_for_install":
      return {
        label: "Confirm or reassign crew",
        route: "/scheduling-dispatch",
      };
    case "unresolved_critical_issue":
      return {
        label: "Resolve urgent issue",
        route: "/issues",
      };
    case "build_review_stalled":
      return {
        label: "Open Build Review",
        instruction:
          "Job is sitting in Build Review and isn't ready for ordering. Approve, request revision, or send back to sales.",
        route: "/intake-review",
      };
    case "payment_not_collected_post_completion":
      return {
        label: "Send payment confirmation text",
        draft: `Hi ${job.customerName.split(" ")[0]} — just confirming payment for your project completed yesterday. Let me know once processed. Thank you.`,
        route: "/messages",
      };
    case "funding_not_confirmed_post_completion":
      return {
        label: "Check financing status",
        instruction:
          "Open job financials and verify funding disbursement status with the financing provider.",
        route: `/jobs/${job.id}`,
      };
  }
}

// ── CCC: Post-completion prediction builder ────────────────────────────
//
// Bypasses the 10-signal scoring pipeline. Post-completion exposure is
// a discrete, deterministic state — either money is missing or it isn't.
// We surface it at high confidence and let the operator close the loop.

function buildPostCompletionPrediction(
  job: Job,
  exception: PostCompletionFinancialException,
): PredictedFailure {
  const mode: PredictedFailureMode =
    exception.kind === "cash_not_collected"
      ? "payment_not_collected_post_completion"
      : "funding_not_confirmed_post_completion";

  const completedDate = new Date(exception.completedAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const amountStr = `$${exception.outstandingAmount.toLocaleString()}`;
  const isCash = exception.kind === "cash_not_collected";

  const headline = isCash
    ? `${amountStr} not collected from ${job.id} (completed ${completedDate})`
    : `Funding not confirmed for ${job.id} (completed ${completedDate})`;

  const evidence: RiskSignal[] = isCash
    ? [
        { key: "completed_yesterday", label: `Completed: ${completedDate}`, weight: 0, present: true },
        { key: "signed", label: "Signed: Yes", weight: 0, present: true },
        { key: "payment_not_received", label: "Payment: Not received", weight: 0, present: true },
        { key: "same_day_expected", label: "Contract: Same-day collection expected", weight: 0, present: true },
      ]
    : [
        { key: "completed_yesterday", label: `Completed: ${completedDate}`, weight: 0, present: true },
        { key: "signed", label: "Signed: Yes", weight: 0, present: true },
        { key: "financing_approved", label: "Financing: Approved", weight: 0, present: true },
        { key: "funding_not_confirmed", label: "Funding: Not confirmed", weight: 0, present: true },
      ];

  return {
    jobId: job.id,
    customerName: job.customerName,
    confidence: 92, // discrete state, high confidence by construction
    failureMode: mode,
    headline,
    predictedBy: exception.completedAt,
    evidence,
    intervention: draftIntervention(mode, job, evidence),
    suppressedFromPrimary: false,
  };
}

// ── Public API ──────────────────────────────────────────────────────────

export function runPreMortem(inputs: PreMortemEngineInputs): PreMortemResult {
  const predictions: PredictedFailure[] = [];
  for (const job of inputs.jobs) {
    const result = evaluateJob(job, inputs);
    if (result) predictions.push(result);
  }
  predictions.sort((a, b) => b.confidence - a.confidence);
  return {
    predictions,
    allClear: predictions.length === 0,
    evaluatedCount: inputs.jobs.length,
  };
}
